import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Play, Camera, Sparkles, ListChecks, Wand2, FileText, BarChart3, Ruler } from 'lucide-react';
import { useLightbox } from './LightboxProvider';
import { isVideoUrl, isPdfUrl, posterFor } from '../lib/media';
import MediaTile from './MediaTile';
import AttachmentsViewer from './AttachmentsViewer';
import { responsiveImgProps } from '../lib/responsiveImage';
import { displayMoney } from '../lib/money';
import InlineRoiField from './InlineRoiField';

const statusConfig = {
  green: { label: 'Reconstruction', color: '#22C55E' },
  yellow: { label: 'Crucial Enhancement', color: '#EAB308' },
  blue: { label: 'Requested Enhancement', color: '#3B82F6' },
  completed: { label: 'Completed', color: '#22C55E' },
  'in-progress': { label: 'In Progress', color: '#EAB308' },
  planned: { label: 'Planned', color: '#3B82F6' },
  urgent: { label: 'Priority', color: '#EF4444' },
};

const completionConfig = {
  planned: { label: 'Planned', sign: '?' },
  'in-progress': { label: 'In Progress', sign: '~' },
  completed: { label: 'Completed', sign: '\u2713' },
};

const zoneLabels = {
  outdoor: 'Resort Grounds',
  indoor: '2nd Floor Plan',
  'back-of-house': 'Back of House',
  rooftop: '4th Floor Rooftop',
};

function ImageCarousel({ images, label, badgeColor, badgeIcon, onClickImage }) {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        {badgeIcon}
        <span className="text-xs font-bold tracking-wider uppercase" style={{ fontFamily: 'Manrope', color: badgeColor }}>{label}</span>
        <span className="text-[10px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>({images.length})</span>
      </div>

      <div className="relative aspect-[16/9] overflow-hidden rounded-xl bg-black">
        {isVideoUrl(images[idx]) ? (
          <video
            key={images[idx]}
            src={images[idx]}
            poster={posterFor(images[idx]) || undefined}
            className="w-full h-full object-contain bg-black"
            controls
            playsInline
            preload="metadata"
          />
        ) : isPdfUrl(images[idx]) ? (
          <a href={images[idx].startsWith('http') ? images[idx] : `${process.env.REACT_APP_BACKEND_URL}${images[idx]}`} target="_blank" rel="noreferrer" className="w-full h-full block">
            <MediaTile src={images[idx]} />
          </a>
        ) : (
          <div className="w-full h-full cursor-zoom-in" onClick={() => onClickImage(images[idx])}>
            <img {...responsiveImgProps(images[idx], '(max-width: 1024px) 100vw, 720px')} alt={`${label} ${idx + 1}`} className="w-full h-full object-cover" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2.5 py-1 rounded-full text-[10px] font-bold text-white pointer-events-none" style={{ backgroundColor: badgeColor }}>
          {label}
        </span>
        {isVideoUrl(images[idx]) && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold text-white pointer-events-none" style={{ backgroundColor: '#EF4444', fontFamily: 'Manrope' }}>VIDEO</span>
        )}
        {isPdfUrl(images[idx]) && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold text-white pointer-events-none" style={{ backgroundColor: '#DC2626', fontFamily: 'Manrope' }}>PDF</span>
        )}
        {images.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setIdx((p) => (p - 1 + images.length) % images.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center z-10">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((p) => (p + 1) % images.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center z-10">
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-1.5 mt-2 overflow-x-auto">
          {images.map((img, i) => {
            const isVid = isVideoUrl(img);
            const isPdf = isPdfUrl(img);
            return (
              <button key={i} onClick={() => setIdx(i)} className="w-14 h-10 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all relative bg-black" style={{ borderColor: i === idx ? badgeColor : 'transparent', opacity: i === idx ? 1 : 0.5 }}>
                {isVid ? (
                  <>
                    <video src={img} poster={posterFor(img) || undefined} className="w-full h-full object-cover" muted preload="metadata" playsInline />
                    <Play className="absolute inset-0 m-auto w-3 h-3 text-white fill-white drop-shadow" />
                  </>
                ) : isPdf ? (
                  <MediaTile src={img} compact />
                ) : (
                  <img {...responsiveImgProps(img, '56px')} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionCard({ icon, title, count, accentColor, children, testId }) {
  return (
    <div data-testid={testId} className="p-4 rounded-xl" style={{ backgroundColor: '#EAE5D9' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accentColor}20`, color: accentColor }}>
          {icon}
        </div>
        <h3 className="text-sm font-bold tracking-wide" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
          {title}
        </h3>
        {typeof count === 'number' && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'white', color: accentColor, fontFamily: 'Manrope' }}>
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function AssigneeChips({ assignees, testidPrefix }) {
  if (!assignees || assignees.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={`${testidPrefix}-chips`}>
      {assignees.slice(0, 4).map((a, i) => (
        <span
          key={`${a.email || a.name}-${i}`}
          className="inline-flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full text-[10px]"
          style={{ backgroundColor: '#EFF6FF', color: '#1F4FBE', fontFamily: 'Manrope' }}
          title={a.email || 'External team member'}
        >
          <span className="w-3.5 h-3.5 rounded-full bg-blue-600 text-white text-[8px] flex items-center justify-center">
            {((a.name || '?').trim()[0] || '?').toUpperCase()}
          </span>
          {a.name}
        </span>
      ))}
      {assignees.length > 4 && (
        <span className="text-[10px] text-gray-500" style={{ fontFamily: 'Manrope' }}>
          +{assignees.length - 4} more
        </span>
      )}
    </div>
  );
}

function TaskRow({ task, idx }) {
  const pct = Math.max(0, Math.min(100, task.progress_percent ?? 0));
  const barColor = pct >= 100 ? '#22C55E' : pct > 0 ? '#EAB308' : '#A3D4D4';
  return (
    <div data-testid={`planned-task-${idx}`} className="flex items-start gap-3 py-1.5">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold truncate" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>{task.title || `Task ${idx + 1}`}</p>
        <div className="w-full h-1.5 mt-1 rounded-full" style={{ backgroundColor: '#FFFFFF' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} />
        </div>
        <AssigneeChips assignees={task.assignees} testidPrefix={`planned-task-${idx}-assignees`} />
      </div>
      <span className="text-xs font-bold flex-shrink-0 tabular-nums" style={{ fontFamily: 'Manrope', color: barColor, minWidth: '2.5rem', textAlign: 'right' }}>
        {pct}%
      </span>
    </div>
  );
}

export default function AreaDetailDialog({ area, open, onClose, onStartTour }) {
  const { open: openLightbox } = useLightbox();
  const [ganttOpen, setGanttOpen] = useState(false);
  const [cadOpen, setCadOpen] = useState(false);
  // iter-125 — Local overrides for inline-edited ROI fields. Lets the
  // dialog reflect saves immediately without round-tripping the parent.
  const [roiOverrides, setRoiOverrides] = useState({});

  if (!area) return null;

  // Apply optimistic ROI overrides so the formatted display refreshes
  // the instant an editor commits a change.
  const eff = { ...area, ...roiOverrides };
  const onRoiSaved = ({ field, value }) => setRoiOverrides((s) => ({ ...s, [field]: value }));

  const colorKey = area.color || area.status || 'green';
  const status = statusConfig[colorKey] || statusConfig.green;
  const comp = completionConfig[area.completion] || completionConfig[area.status] || completionConfig.planned;
  const zoneLabel = zoneLabels[area.zone] || area.zone;
  const progress = area.progress_percent || 0;

  const renders = area.renders || [];
  const photos = area.images && area.images.length > 0 ? area.images : [area.before_image, area.after_image].filter(Boolean);
  const allLightboxImages = [...renders, ...photos].filter((u) => !isVideoUrl(u) && !isPdfUrl(u));
  const enhancements = area.enhancements || [];
  const ganttCharts = area.gantt_charts || [];
  const autocadPlans = area.autocad_plans || [];
  const milestones = area.milestones || [];
  const hasGantt = ganttCharts.length > 0 || milestones.length > 0;
  const hasTour = (renders.length > 0 || photos.length > 0) && onStartTour;

  const showImage = (img) => openLightbox(img, allLightboxImages);

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          data-testid="area-detail-dialog"
          className="w-[95vw] max-w-6xl max-h-[92vh] overflow-y-auto p-0 rounded-xl sm:rounded-2xl"
          style={{ backgroundColor: '#F4F1EB' }}
        >
          <div className="p-4 sm:p-6 md:p-8">
            <DialogHeader className="mb-4 sm:mb-6">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-bold text-white" style={{ backgroundColor: status.color }}>
                  {status.label}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-bold" style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D' }}>
                  {comp.sign} {comp.label}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-bold" style={{ backgroundColor: '#1A2E2E', color: 'white' }}>
                  {progress}%
                </span>
                <span className="text-[10px] sm:text-xs tracking-[0.1em] uppercase font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope, sans-serif' }}>
                  {zoneLabel}
                </span>
              </div>

              <div className="w-full h-2 rounded-full mb-3" style={{ backgroundColor: '#EAE5D9' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: status.color }} />
              </div>

              <DialogTitle className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
                {area.name}
              </DialogTitle>
            </DialogHeader>

            {/* Two-column layout: pictures (left) + info sections (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7 mb-5">
              {/* LEFT: Pictures */}
              <div>
                <ImageCarousel
                  images={renders}
                  label="After — Renders"
                  badgeColor="#22C55E"
                  badgeIcon={<Sparkles className="w-3.5 h-3.5" style={{ color: '#22C55E' }} />}
                  onClickImage={showImage}
                />
                <ImageCarousel
                  images={photos}
                  label="Before — Photos"
                  badgeColor="#EF4444"
                  badgeIcon={<Camera className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />}
                  onClickImage={showImage}
                />
                {renders.length === 0 && photos.length === 0 && (
                  <div className="p-8 text-center rounded-xl" style={{ backgroundColor: '#EAE5D9', color: '#7A8C8C', fontFamily: 'Manrope' }}>
                    <Camera className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">No photos or renders yet.</p>
                  </div>
                )}
              </div>

              {/* RIGHT: 3 sections */}
              <div className="space-y-4">
                {/* Section 1: Planned Tasks — grouped by kind */}
                {(() => {
                  const enhs = enhancements.filter((e) => (e.kind || 'repair') === 'enhancement');
                  const reps = enhancements.filter((e) => (e.kind || 'repair') !== 'enhancement');
                  return (
                    <SectionCard
                      testId="planned-tasks-section"
                      icon={<ListChecks className="w-4 h-4" />}
                      title="Planned Tasks"
                      count={enhancements.length}
                      accentColor="#1F4FBE"
                    >
                      {enhancements.length === 0 ? (
                        <p className="text-xs" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>No tasks defined yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {enhs.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5 flex items-center gap-1.5" style={{ color: '#EF4444', fontFamily: 'Manrope' }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#EF4444', boxShadow: '0 0 8px #EF4444' }} />
                                Enhancements ({enhs.length})
                              </p>
                              <div className="space-y-2">
                                {enhs.map((t, i) => (
                                  <div
                                    key={`enh-${i}`}
                                    className="rounded-lg overflow-hidden"
                                    style={{ boxShadow: '0 0 0 2px #EF4444, 0 0 14px rgba(239,68,68,0.35)' }}
                                  >
                                    <TaskRow task={t} idx={i} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {reps.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                                Repairs ({reps.length})
                              </p>
                              <div className="divide-y" style={{ borderColor: 'rgba(0,61,165,0.15)' }}>
                                {reps.map((t, i) => (
                                  <TaskRow key={`rep-${i}`} task={t} idx={i} />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </SectionCard>
                  );
                })()}

                {/* Section 2: Proposed Enhancements (only items with kind === 'enhancement') */}
                {(() => {
                  const enhs = enhancements.filter((e) => (e.kind || 'repair') === 'enhancement');
                  return (
                    <SectionCard
                      testId="proposed-enhancements-section"
                      icon={<Wand2 className="w-4 h-4" />}
                      title="Proposed Enhancements"
                      count={enhs.length}
                      accentColor="#EF4444"
                    >
                      {enhs.length > 0 ? (
                        <div className="space-y-2.5">
                          {enhs.map((e, i) => (
                            <div
                              key={i}
                              data-testid={`proposed-enh-${i}`}
                              className="p-2.5 rounded-lg"
                              style={{ backgroundColor: '#FEF2F2', boxShadow: '0 0 0 1.5px #EF4444, 0 0 12px rgba(239,68,68,0.3)' }}
                            >
                              <p className="text-xs font-bold mb-0.5" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
                                {e.title || `Enhancement ${i + 1}`}
                              </p>
                              <p className="text-xs leading-relaxed" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
                                {e.description || <span style={{ color: '#A3A3A3', fontStyle: 'italic' }}>No description provided.</span>}
                              </p>
                              <AssigneeChips assignees={e.assignees} testidPrefix={`proposed-enh-${i}-assignees`} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                          No enhancements proposed — only repair tasks in this area.
                        </p>
                      )}
                    </SectionCard>
                  );
                })()}

                {/* Section 3: Area Summary + Enhancements ROI */}
                <SectionCard
                  testId="area-summary-section"
                  icon={<FileText className="w-4 h-4" />}
                  title="Area Summary"
                  accentColor="#FF6F59"
                >
                  <p className="text-xs sm:text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif', color: '#4A5D5D' }}>
                    {area.description || <span style={{ color: '#A3A3A3', fontStyle: 'italic' }}>No summary provided.</span>}
                  </p>
                  {area.enhancements_roi && (
                    <div
                      data-testid="enhancements-roi-box"
                      className="mt-3 p-3 rounded-lg"
                      style={{
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #FCA5A5',
                        boxShadow: '0 0 10px rgba(239,68,68,0.15)',
                      }}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5 flex items-center gap-1.5" style={{ color: '#EF4444', fontFamily: 'Manrope' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#EF4444', boxShadow: '0 0 6px #EF4444' }} />
                        Proposed Enhancements and ROI
                      </p>
                      <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
                        {area.enhancements_roi}
                      </p>
                    </div>
                  )}
                  {/* iter-110 — Structured ROI fields (Investment / Return /
                      Timeline / Scope / Strategic benefits). Saved per area
                      and viewable in user-access here. Renders only when at
                      least one field has content. */}
                  {/* iter-125 — Always render the ROI box for editors so they
                      can fill in the first value inline. Non-editors only see
                      the box when at least one field has content. */}
                  {(eff.roi_investment || eff.roi_return || eff.roi_timeline || eff.roi_scope || eff.roi_benefits
                    || (typeof window !== 'undefined' && ['editor','admin','engineer','ownership','top_leader'].includes((localStorage.getItem('user_role') || '').toLowerCase()))) && (
                    <div
                      data-testid="roi-analysis-box"
                      className="mt-3 p-3 rounded-lg"
                      style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2 flex items-center gap-1.5" style={{ color: '#B45309', fontFamily: 'Manrope' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#B45309' }} />
                        ROI Analysis
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="rounded-md p-2" style={{ background: 'white', border: '1px solid #FDE68A' }}>
                          <InlineRoiField areaId={area.id} field="roi_investment" value={eff.roi_investment} label="Investment" isMoney onSaved={onRoiSaved} placeholder="$ amount or range" testid="roi-investment" />
                        </div>
                        <div className="rounded-md p-2" style={{ background: 'white', border: '1px solid #FDE68A' }}>
                          <InlineRoiField areaId={area.id} field="roi_return" value={eff.roi_return} label="Return" isMoney onSaved={onRoiSaved} placeholder="$ annual return / IRR" testid="roi-return" />
                        </div>
                        <div className="rounded-md p-2" style={{ background: 'white', border: '1px solid #FDE68A' }}>
                          <InlineRoiField areaId={area.id} field="roi_timeline" value={eff.roi_timeline} label="Timeline" onSaved={onRoiSaved} placeholder="e.g. 12 months" testid="roi-timeline" />
                        </div>
                        <div className="rounded-md p-2" style={{ background: 'white', border: '1px solid #FDE68A' }}>
                          <InlineRoiField areaId={area.id} field="roi_scope" value={eff.roi_scope} label="Scope" onSaved={onRoiSaved} placeholder="What's included" testid="roi-scope" />
                        </div>
                        <div className="rounded-md p-2 sm:col-span-2" style={{ background: 'white', border: '1px solid #FDE68A' }}>
                          <InlineRoiField areaId={area.id} field="roi_benefits" value={eff.roi_benefits} label="Strategic Benefits" onSaved={onRoiSaved} placeholder="Why this matters" testid="roi-benefits" />
                        </div>
                      </div>
                    </div>
                  )}
                  {area.notes && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                      <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Notes</p>
                      <p className="text-xs leading-relaxed" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>{area.notes}</p>
                    </div>
                  )}
                </SectionCard>
              </div>
            </div>

            {/* Gantt Chart & AutoCAD Plans — always visible so admins can see the "Upload via Admin" hint */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <button
                data-testid="view-gantt-btn"
                onClick={() => setGanttOpen(true)}
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
                style={{ backgroundColor: '#FF6F59', fontFamily: 'Manrope', boxShadow: '0 4px 15px rgba(255,111,89,0.25)' }}
              >
                <BarChart3 className="w-4 h-4" />
                {hasGantt
                  ? `Gantt Chart (${ganttCharts.length || milestones.length})`
                  : 'View Gantt Chart'}
              </button>
              <button
                data-testid="view-cad-btn"
                onClick={() => setCadOpen(true)}
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
                style={{ backgroundColor: '#3B82F6', fontFamily: 'Manrope', boxShadow: '0 4px 15px rgba(59,130,246,0.25)' }}
              >
                <Ruler className="w-4 h-4" />
                {autocadPlans.length > 0 ? `AutoCAD Plans (${autocadPlans.length})` : 'View AutoCAD Plans'}
              </button>
            </div>

            {/* Virtual Tour button — full-width below both columns */}
            {hasTour && (
              <button
                data-testid="area-tour-btn"
                onClick={() => { onClose(false); onStartTour(area); }}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: '#8B5CF6', fontFamily: 'Manrope', boxShadow: '0 4px 15px rgba(139,92,246,0.3)' }}
              >
                <Play className="w-4 h-4" />
                Virtual Tour of {area.name}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <AttachmentsViewer
        open={ganttOpen}
        onClose={() => setGanttOpen(false)}
        items={ganttCharts}
        milestones={milestones}
        title={`Gantt Chart — ${area.name}`}
        accentColor="#FF6F59"
        kind="gantt"
      />
      <AttachmentsViewer
        open={cadOpen}
        onClose={() => setCadOpen(false)}
        items={autocadPlans}
        title={`AutoCAD Plans — ${area.name}`}
        accentColor="#3B82F6"
        kind="cad"
      />
    </>
  );
}
